import axios from 'axios';
import { AppConfig, getApiUrl } from 'config/appConfig';
import { ResolvedTenant } from 'types/tenant';
import { readTenantFromToken } from 'utils/jwtClaims';
import { ENDPOINTS } from 'api/endpoints';

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

const isLocalHost = (hostname: string): boolean => LOCAL_HOSTS.has(hostname.toLowerCase());

const parseSubdomainTenantCode = (hostname: string, baseDomain?: string): string | null => {
  if (!baseDomain) return null;
  const normalizedBase = baseDomain.toLowerCase();
  const normalizedHost = hostname.toLowerCase();
  const suffix = `.${normalizedBase}`;
  if (!normalizedHost.endsWith(suffix)) return null;
  const prefix = normalizedHost.slice(0, -suffix.length);
  const code = prefix.split('.').filter(Boolean)[0];
  return code || null;
};

const resolveFromApi = async (
  hostname: string,
  baseDomain?: string
): Promise<ResolvedTenant | null> => {
  try {
    const response = await axios.get<ResolvedTenant>(`${getApiUrl()}${ENDPOINTS.TENANT_RESOLVE}`, {
      params: { host: hostname, baseDomain },
    });
    return response.data;
  } catch {
    return null;
  }
};

const resolveFromConfigFallback = (config: AppConfig, hostname: string): ResolvedTenant | null => {
  if (!isLocalHost(hostname) || !config.tenantCode) return null;
  return {
    tenantId: 0,
    tenantCode: config.tenantCode,
    siteName: config.siteName,
  };
};

const tenantResolverService = {
  resolve: async (config: AppConfig): Promise<ResolvedTenant | null> => {
    const hostname = window.location.hostname;

    const fromToken = readTenantFromToken();
    if (fromToken) {
      return {
        tenantId: fromToken.tenantId,
        tenantCode: fromToken.tenantCode,
        siteName: config.siteName,
      };
    }

    const fromApi = await resolveFromApi(hostname, config.tenantBaseDomain);
    if (fromApi) return fromApi;

    const subdomainCode = parseSubdomainTenantCode(hostname, config.tenantBaseDomain);
    if (subdomainCode) {
      return {
        tenantId: 0,
        tenantCode: subdomainCode,
        siteName: config.siteName,
      };
    }

    return resolveFromConfigFallback(config, hostname);
  },
};

export default tenantResolverService;
